import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  DocumentKindSchema,
  type Document,
  type DocumentKind,
  type Project,
  type Space,
} from "@scenix/core";
import { ApiError, api } from "../lib/api";
import { allBut } from "../lib/enums";
import { RECOVERY_WINDOW_DAYS } from "../lib/recovery";
import { Markdown } from "./Markdown";
import { RecoveryCountdown } from "./RecoveryCountdown";

interface DocumentsPanelProps {
  spaceId: Space["id"];
  projectId: Project["id"];
  mayEdit: boolean;
}

// Every kind but the treatment, which is a role assigned at project creation
// rather than a kind a person picks here (ADR 000011).
const CREATABLE_KINDS = allBut(DocumentKindSchema.options, ["treatment"]);

/**
 * Every document a project holds beyond its treatment. The API has always
 * supported script, outline, shot_list and note documents; this is where
 * they stop dead-ending at "no way to click" (issue #98).
 */
export function DocumentsPanel({ spaceId, projectId, mayEdit }: DocumentsPanelProps) {
  const queryClient = useQueryClient();
  const documentsKey = ["documents", spaceId, projectId];

  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newKind, setNewKind] = useState<DocumentKind>("script");

  const [editingId, setEditingId] = useState<Document["id"] | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [conflict, setConflict] = useState<string | null>(null);
  const [showingDeleted, setShowingDeleted] = useState(false);

  const documents = useQuery({
    queryKey: documentsKey,
    queryFn: () => api.listDocuments(spaceId, projectId),
  });

  // Only fetched once somebody looks. A project that never deleted anything
  // should not pay a request per visit for an empty list (ADR 000076).
  const deletedDocuments = useQuery({
    queryKey: [...documentsKey, "deleted"],
    queryFn: () => api.listDocuments(spaceId, projectId, { deleted: true }),
    enabled: showingDeleted,
  });

  // A prefix, so it reaches both sides of the recovery window: the deleted
  // listing hangs off the same key with one more segment (ADR 000076).
  const invalidate = () => queryClient.invalidateQueries({ queryKey: documentsKey });

  const create = useMutation({
    mutationFn: () =>
      api.createDocument(spaceId, projectId, {
        title: newTitle.trim(),
        body: "",
        kind: newKind,
        baseRevision: 0,
      }),
    onSuccess: async () => {
      setNewTitle("");
      setCreating(false);
      await invalidate();
    },
  });

  const save = useMutation({
    mutationFn: (document: Document) =>
      api.updateDocument(spaceId, projectId, document.id, {
        title: title.trim(),
        body,
        kind: document.kind,
        baseRevision: document.revision,
      }),
    onSuccess: async () => {
      setEditingId(null);
      setConflict(null);
      await invalidate();
    },
    onError: (error) => {
      if (error instanceof ApiError && error.isConflict) {
        // Somebody else's edit landed first; re-read and reconcile rather
        // than retry with the same base revision (ADR 000027).
        setEditingId(null);
        setConflict(
          "Somebody else changed this document while you were editing. Reloaded it; your edit was not saved.",
        );
        void invalidate();
      }
    },
  });

  const remove = useMutation({
    mutationFn: (document: Document) => api.deleteDocument(spaceId, projectId, document.id),
    onSuccess: async () => {
      setEditingId(null);
      await invalidate();
    },
  });

  const restore = useMutation({
    mutationFn: (document: Document) => api.restoreDocument(spaceId, projectId, document.id),
    onSuccess: async () => invalidate(),
  });

  if (documents.isPending) return <p role="status">Loading documents…</p>;
  if (documents.error) {
    return (
      <p className="error">
        {documents.error instanceof ApiError
          ? documents.error.userMessage
          : documents.error.message}
      </p>
    );
  }

  const others = documents.data.filter((document) => document.kind !== "treatment");

  function startEditing(document: Document) {
    setEditingId(document.id);
    setTitle(document.title);
    setBody(document.body);
    setConflict(null);
  }

  return (
    <section>
      <div className="section-bar">
        <h2 className="section-head">Documents</h2>
        {mayEdit && !creating && (
          <button type="button" className="ghost" onClick={() => setCreating(true)}>
            New document
          </button>
        )}
        <button
          type="button"
          className="ghost"
          onClick={() => setShowingDeleted((showing) => !showing)}
        >
          {showingDeleted ? "Hide deleted" : "Deleted"}
        </button>
      </div>

      {conflict && (
        <p role="alert" className="error">
          {conflict}
        </p>
      )}
      {create.isError && (
        <p role="alert" className="error">
          {create.error instanceof ApiError ? create.error.userMessage : create.error.message}
        </p>
      )}
      {/* Whatever the API refused with, said out loud. The treatment is not
          offered here at all, since this panel lists everything but it, but a
          key that deleted one out from under the tab lands in this line. */}
      {remove.isError && (
        <p role="alert" className="error">
          {remove.error instanceof ApiError ? remove.error.userMessage : remove.error.message}
        </p>
      )}
      {restore.isError && (
        <p role="alert" className="error">
          {restore.error instanceof ApiError ? restore.error.userMessage : restore.error.message}
        </p>
      )}

      {showingDeleted && (
        <div className="deleted-documents">
          <h3 className="section-head">Deleted</h3>
          {/* The shape of the promise before somebody needs it. A script or an
              outline exists nowhere else and cannot be uploaded again, so
              "Deleted" reading as an archive is worse here than on the corpus
              (ADR 000055, ADR 000076). */}
          <p className="muted">
            A deleted document can be brought back for {RECOVERY_WINDOW_DAYS} days. After that it is
            removed for good, and nothing here can bring it back.
          </p>
          {deletedDocuments.isPending ? (
            <p role="status">Loading…</p>
          ) : deletedDocuments.error ? (
            <p className="error">
              {deletedDocuments.error instanceof ApiError
                ? deletedDocuments.error.userMessage
                : deletedDocuments.error.message}
            </p>
          ) : deletedDocuments.data.length === 0 ? (
            <p className="muted">Nothing deleted right now.</p>
          ) : (
            <ul className="documents-list">
              {deletedDocuments.data.map((document) => (
                <li key={document.id} className="compose-row">
                  <span className="document-kind">{document.kind.replace("_", " ")}</span>
                  <span>{document.title}</span>
                  <RecoveryCountdown deletedAt={document.deletedAt} />
                  {mayEdit && (
                    <button
                      type="button"
                      className="ghost"
                      disabled={restore.isPending}
                      onClick={() => restore.mutate(document)}
                    >
                      Restore
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {creating && (
        <form
          className="compose-row"
          onSubmit={(event) => {
            event.preventDefault();
            if (newTitle.trim()) create.mutate();
          }}
        >
          <select
            aria-label="Kind"
            value={newKind}
            onChange={(event) => setNewKind(event.target.value as DocumentKind)}
          >
            {CREATABLE_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {kind.replace("_", " ")}
              </option>
            ))}
          </select>
          <input
            aria-label="Title"
            placeholder="Title"
            value={newTitle}
            maxLength={300}
            onChange={(event) => setNewTitle(event.target.value)}
            autoFocus
          />
          <button type="submit" disabled={!newTitle.trim() || create.isPending}>
            {create.isPending ? "Creating…" : "Create"}
          </button>
          <button type="button" className="ghost" onClick={() => setCreating(false)}>
            Cancel
          </button>
        </form>
      )}

      {others.length === 0 ? (
        <p className="muted">No other documents yet.</p>
      ) : (
        <ul className="documents-list">
          {others.map((document) =>
            editingId === document.id ? (
              <li key={document.id} className="document-list-item">
                <form
                  className="document-edit"
                  onSubmit={(event) => {
                    event.preventDefault();
                    save.mutate(document);
                  }}
                >
                  <label htmlFor={`doc-title-${document.id}`}>Title</label>
                  <input
                    id={`doc-title-${document.id}`}
                    value={title}
                    maxLength={300}
                    onChange={(event) => setTitle(event.target.value)}
                  />
                  <label htmlFor={`doc-body-${document.id}`}>Body</label>
                  <textarea
                    id={`doc-body-${document.id}`}
                    className="document-body-input"
                    value={body}
                    rows={12}
                    onChange={(event) => setBody(event.target.value)}
                  />
                  <div className="compose-row">
                    <button type="submit" disabled={!title.trim() || save.isPending}>
                      {save.isPending ? "Saving…" : "Save"}
                    </button>
                    <button type="button" className="ghost" onClick={() => setEditingId(null)}>
                      Cancel
                    </button>
                  </div>
                </form>
              </li>
            ) : (
              <li key={document.id} className="document-list-item">
                <div className="row">
                  <span className="document-kind">{document.kind.replace("_", " ")}</span>
                  <h3>{document.title}</h3>
                  {mayEdit && (
                    <button type="button" className="ghost" onClick={() => startEditing(document)}>
                      Open
                    </button>
                  )}
                  {mayEdit && (
                    <button
                      type="button"
                      className="ghost"
                      aria-label={`Delete ${document.title}`}
                      disabled={remove.isPending}
                      onClick={() => {
                        // Asked before, and the question says where it goes:
                        // recoverable is only a comfort to somebody who knows
                        // it is (ADR 000076).
                        if (
                          window.confirm(
                            `Delete "${document.title}"? You can restore it from Deleted later.`,
                          )
                        ) {
                          remove.mutate(document);
                        }
                      }}
                    >
                      Delete
                    </button>
                  )}
                </div>
                <Markdown>{document.body}</Markdown>
                <p className="muted">Revision {document.revision}</p>
              </li>
            ),
          )}
        </ul>
      )}
    </section>
  );
}
